import { useState, useRef } from 'react';
import { motion, useInView } from 'framer-motion';

export default function Process() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  const [activeStep, setActiveStep] = useState(0);

  const steps = [
    {
      number: '01',
      title: 'Análisis del Sitio Web',
      icon: 'ri-search-line',
      description: 'Auditoría técnica completa de tu sitio web actual',
      details: [
        'Análisis de arquitectura y estructura',
        'Evaluación de Core Web Vitals',
        'Revisión de Schema Markup existente',
        'Análisis de enlaces internos y externos',
        'Evaluación de contenido y keywords',
        'Identificación de oportunidades de mejora'
      ],
      color: 'cyan-500'
    },
    {
      number: '02',
      title: 'Informe de Modificaciones',
      icon: 'ri-file-list-3-line',
      description: 'Documento detallado con todas las optimizaciones planificadas',
      details: [
        'Priorización de acciones por impacto',
        'Roadmap de implementación',
        'Estimación de resultados esperados',
        'Cronograma de ejecución',
        'Recursos necesarios',
        'KPIs y métricas de seguimiento'
      ],
      color: 'purple-500'
    },
    {
      number: '03',
      title: 'Actuación Automatizada',
      icon: 'ri-settings-3-line',
      description: 'Implementación de todas las optimizaciones con IA',
      details: [
        'Implementación de Schema Markup',
        'Optimización de velocidad y caché',
        'Configuración de PWA',
        'Optimización para agentes IA',
        'Mejora de enlaces internos',
        'Actualización continua automática'
      ],
      color: 'green-500'
    },
    {
      number: '04',
      title: 'Panel de Seguimiento',
      icon: 'ri-dashboard-3-line',
      description: 'Acceso 24/7 a tu panel personalizado de métricas',
      details: [
        'Monitorización en tiempo real',
        'Gráficos de evolución de rankings',
        'Análisis de tráfico orgánico',
        'Core Web Vitals actualizados',
        'Alertas automáticas',
        'Informes mensuales detallados'
      ],
      color: 'orange-500'
    }
  ];

  return (
    <section ref={ref} className="py-16 sm:py-24 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="mb-12 sm:mb-16"
        >
          <div className="flex items-center gap-4 mb-4 px-4">
            <div className="h-1 w-16 sm:w-20 bg-cyan-500 rounded-full"></div>
            <span className="text-base sm:text-lg font-medium text-cyan-500">Nuestro Proceso</span>
          </div>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-black mb-4 px-4">
            Cómo <span className="text-cyan-500">Trabajamos</span>
          </h2>
          <p className="text-base sm:text-lg md:text-xl text-gray-600 max-w-3xl px-4">
            Un proceso claro, transparente y automatizado que garantiza resultados excepcionales
          </p>
        </motion.div>

        {/* Timeline */}
        <div className="relative">
          {/* Connection Line - Desktop only */}
          <div className="hidden lg:block absolute top-24 left-0 right-0 h-1 bg-cyan-500 rounded-full"></div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8">
            {steps.map((step, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                animate={isInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.5, delay: index * 0.15 }}
                className="relative"
                onMouseEnter={() => setActiveStep(index)}
                onClick={() => setActiveStep(index)}
              >
                {/* Step Circle */}
                <div className="flex justify-center mb-4 sm:mb-6">
                  <button
                    className={`w-20 h-20 sm:w-24 sm:h-24 rounded-full flex items-center justify-center transition-all duration-300 relative z-10 ${
                      activeStep === index
                        ? `bg-${step.color} shadow-2xl scale-110`
                        : 'bg-white border-4 border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <i className={`${step.icon} text-2xl sm:text-3xl ${
                      activeStep === index ? 'text-white' : 'text-gray-400'
                    }`}></i>
                  </button>
                </div>

                {/* Step Content */}
                <div className={`bg-white rounded-2xl p-4 sm:p-6 shadow-lg transition-all duration-300 border-2 ${
                  activeStep === index
                    ? 'border-transparent shadow-2xl -translate-y-2'
                    : 'border-gray-100'
                }`}>
                  <div className={`text-4xl sm:text-5xl md:text-6xl font-bold mb-2 ${
                    activeStep === index
                      ? `text-${step.color}`
                      : 'text-gray-200'
                  }`}>
                    {step.number}
                  </div>
                  <h3 className="text-lg sm:text-xl font-bold text-gray-800 mb-2">{step.title}</h3>
                  <p className="text-gray-600 text-xs sm:text-sm mb-4">{step.description}</p>

                  {/* Details - Show only for active step */}
                  {activeStep === index && (
                    <div className="space-y-2 animate-fadeIn">
                      {step.details.map((detail, idx) => (
                        <div key={idx} className="flex items-start gap-2">
                          <div className={`w-4 h-4 sm:w-5 sm:h-5 rounded-md flex items-center justify-center bg-${step.color} flex-shrink-0 mt-0.5`}>
                            <i className="ri-check-line text-white text-xs"></i>
                          </div>
                          <span className="text-xs sm:text-sm text-gray-700">{detail}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </motion.div>
            ))}
          </div>
        </div>

        {/* CTA Section */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.8 }}
          className="mt-16 sm:mt-20 bg-purple-600 rounded-3xl p-8 sm:p-12 text-center text-white relative overflow-hidden"
        >
          <div className="absolute inset-0 bg-black/10"></div>
          <div className="relative z-10">
            <h3 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-4">¿Listo para Transformar tu SEO?</h3>
            <p className="text-base sm:text-lg md:text-xl mb-6 sm:mb-8 opacity-90 max-w-2xl mx-auto px-4">
              Comienza hoy con una auditoría gratuita y descubre el potencial de tu sitio web
            </p>
            <a href="#contacto" className="inline-flex items-center gap-2 px-6 sm:px-8 py-3 sm:py-4 bg-white text-gray-800 rounded-full font-bold text-base sm:text-lg hover:shadow-2xl hover:scale-105 transition-all duration-300 whitespace-nowrap cursor-pointer">
              Solicitar Auditoría Gratuita
              <i className="ri-arrow-right-line"></i>
            </a>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
